import { motion } from 'framer-motion'
import { cn } from '../../utils/helpers'
import { staggerContainer, fadeInUp } from '../../hooks/useAnimations'

const Section = ({
  children,
  className = '',
  dark = false,
  id,
  ...props
}) => {
  return (
    <section
      id={id}
      className={cn(
        'py-16 md:py-24',
        dark ? 'bg-dark text-white' : 'bg-white',
        className
      )}
      {...props}
    >
      {children}
    </section>
  )
}

Section.Container = ({ children, className = '', size = 'default' }) => {
  const sizes = {
    narrow: 'max-w-4xl',
    default: 'max-w-7xl',
    wide: 'max-w-[1400px]',
    full: 'max-w-full',
  }

  return (
    <div className={cn('mx-auto px-4 sm:px-6 lg:px-8', sizes[size], className)}>
      {children}
    </div>
  )
}

Section.Header = ({
  title,
  subtitle,
  description,
  align = 'center',
  className = '',
  dark = false,
}) => {
  const alignClasses = {
    left: 'text-left',
    center: 'text-center mx-auto',
    right: 'text-right ml-auto',
  }

  return (
    <motion.div
      className={cn('max-w-3xl mb-12 md:mb-16', alignClasses[align], className)}
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5 }}
    >
      {subtitle && (
        <span className="inline-block text-secondary-600 font-accent font-semibold text-sm uppercase tracking-wider mb-3">
          {subtitle}
        </span>
      )}
      <h2
        className={cn(
          'text-3xl md:text-4xl lg:text-5xl font-heading font-bold',
          dark ? 'text-white' : 'text-dark-100'
        )}
      >
        {title}
      </h2>
      {description && (
        <p
          className={cn(
            'mt-4 text-lg',
            dark ? 'text-gray-300' : 'text-gray-600'
          )}
        >
          {description}
        </p>
      )}
    </motion.div>
  )
}

Section.Grid = ({
  children,
  cols = 3,
  gap = 'default',
  className = '',
  animate = true,
}) => {
  const colClasses = {
    1: 'grid-cols-1',
    2: 'grid-cols-1 md:grid-cols-2',
    3: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3',
    4: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-4',
  }

  const gapClasses = {
    none: 'gap-0',
    sm: 'gap-4',
    default: 'gap-6 md:gap-8',
    lg: 'gap-8 md:gap-12',
  }

  const Wrapper = animate ? motion.div : 'div'

  return (
    <Wrapper
      className={cn('grid', colClasses[cols], gapClasses[gap], className)}
      variants={animate ? staggerContainer : undefined}
      initial={animate ? 'initial' : undefined}
      whileInView={animate ? 'animate' : undefined}
      viewport={{ once: true }}
    >
      {children}
    </Wrapper>
  )
}

Section.Divider = ({ className = '' }) => (
  <div
    className={cn(
      'h-px bg-gradient-to-r from-transparent via-gray-300 to-transparent my-16',
      className
    )}
  />
)

export default Section
